import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import {
  BILLING_TYPES,
  BillingType,
  EffectiveMode,
  PERIOD_UNITS,
  PRODUCT_STATUSES,
  PeriodUnit,
  ProductStatus,
  type I18nText,
  type ProductPeriod,
  type SubscriptionProduct,
} from '@/api/products'

dayjs.extend(utc)

export const WALL = 'YYYY-MM-DD HH:mm'
export { BILLING_TYPES as BILLING, PERIOD_UNITS }

export const I18nLang = {
  En: 'en',
  ZhCN: 'zh-CN',
  ZhTW: 'zh-TW',
  Vi: 'vi',
} as const
export type I18nLang = (typeof I18nLang)[keyof typeof I18nLang]
export const I18N_DEFAULT = I18nLang.En
export const I18N_LANGS = [I18nLang.En, I18nLang.ZhCN, I18nLang.ZhTW, I18nLang.Vi] as const

export const I18N_LANG_LABEL = {
  [I18nLang.En]: 'products.i18nEn',
  [I18nLang.ZhCN]: 'products.i18nZhCN',
  [I18nLang.ZhTW]: 'products.i18nZhTW',
  [I18nLang.Vi]: 'products.i18nVi',
} as const

export const ListMode = {
  Now: 'now',
  At: 'at',
} as const
export type ListMode = (typeof ListMode)[keyof typeof ListMode]

export const UnlistMode = {
  Never: 'never',
  At: 'at',
} as const
export type UnlistMode = (typeof UnlistMode)[keyof typeof UnlistMode]

export const FieldError = {
  Name: 'name',
  PlanPrice: 'planPrice',
  Periods: 'periods',
  Tiers: 'tiers',
  Modules: 'modules',
  EffectiveAt: 'effective_at',
  ListAt: 'list_at',
  UnlistAt: 'unlist_at',
} as const
export type FieldError = (typeof FieldError)[keyof typeof FieldError]

export const RowError = {
  TierName: 'tier-name',
  TierPrice: 'tier-price',
  TierExpire: 'tier-exp',
  ModuleName: 'mod-name',
  ModulePrice: 'mod-price',
} as const
export type RowError = (typeof RowError)[keyof typeof RowError]

export function rowError(kind: RowError, index: number) {
  return `${kind}-${index}`
}

export type NamePath = (string | number)[]

export function errorKeyToName(key: string): NamePath {
  if (key === FieldError.Name) return ['name', I18N_DEFAULT]
  if (key === FieldError.PlanPrice) return [FieldError.PlanPrice]
  if (key === FieldError.Periods) return [FieldError.Periods]
  if (key === FieldError.Tiers) return [FieldError.Tiers]
  if (key === FieldError.Modules) return [FieldError.Modules]
  if (key === FieldError.EffectiveAt) return [FieldError.EffectiveAt]
  if (key === FieldError.ListAt) return [FieldError.ListAt]
  if (key === FieldError.UnlistAt) return [FieldError.UnlistAt]
  const row = /^(tier-name|tier-price|tier-exp|mod-name|mod-price)-(\d+)$/.exec(key)
  if (!row) return [key]
  const index = Number(row[2])
  if (row[1] === RowError.TierName) return ['tiers', index, 'name', I18N_DEFAULT]
  if (row[1] === RowError.TierPrice) return ['tiers', index, 'price']
  if (row[1] === RowError.TierExpire) return ['tiers', index, 'expire_value']
  if (row[1] === RowError.ModuleName) return ['modules', index, 'name', I18N_DEFAULT]
  return ['modules', index, 'price']
}

export type FormTier = {
  key: string
  id?: string
  name: I18nText
  description: I18nText
  price: string
  expire_value: string
  expire_unit: PeriodUnit
}

export type FormModule = {
  key: string
  id?: string
  name: I18nText
  description: I18nText
  price: string
}

export type FormState = {
  name: I18nText
  description: I18nText
  billing_type: BillingType
  effective_mode: EffectiveMode
  effective_at: string | null
  planPrice: string
  tiers: FormTier[]
  modules: FormModule[]
  periods: ProductPeriod[]
  listNow: boolean
  list_at: string | null
  unlistOn: boolean
  unlist_at: string | null
}

export const PublishIssue = {
  NeedTier: 'needTier',
  NeedModule: 'needModule',
  NeedPeriod: 'needPeriod',
  EffectiveAtFuture: 'effectiveAtFuture',
  ListAtFuture: 'listAtFuture',
  UnlistAfterNow: 'unlistAfterNow',
  UnlistAfterList: 'unlistAfterList',
  NameUnique: 'nameUnique',
  Required: 'required',
} as const
export type PublishIssue = (typeof PublishIssue)[keyof typeof PublishIssue]

export const SaveToastKey = {
  DraftSaved: 'draftSaved',
  SaveBoth: 'saveBoth',
  SavePrice: 'savePrice',
  SaveOther: 'saveOther',
  UnlistSaved: 'unlistSaved',
} as const
export type SaveToastKey = (typeof SaveToastKey)[keyof typeof SaveToastKey]

export type SaveToast =
  | { key: Exclude<SaveToastKey, typeof SaveToastKey.UnlistSaved> }
  | { key: typeof SaveToastKey.UnlistSaved; time: string }

export const PUBLISH_ISSUE_KEY = {
  [PublishIssue.NeedTier]: 'products.needTier',
  [PublishIssue.NeedModule]: 'products.needModule',
  [PublishIssue.NeedPeriod]: 'products.needPeriod',
  [PublishIssue.EffectiveAtFuture]: 'products.effectiveAtFuture',
  [PublishIssue.ListAtFuture]: 'products.listAtFuture',
  [PublishIssue.UnlistAfterNow]: 'products.unlistAfterNow',
  [PublishIssue.UnlistAfterList]: 'products.unlistAfterList',
  [PublishIssue.NameUnique]: 'products.nameUnique',
  [PublishIssue.Required]: 'products.required',
} as const

export const SAVE_TOAST_KEY = {
  [SaveToastKey.DraftSaved]: 'products.draftSaved',
  [SaveToastKey.SaveBoth]: 'products.saveBoth',
  [SaveToastKey.SavePrice]: 'products.savePrice',
  [SaveToastKey.SaveOther]: 'products.saveOther',
} as const

export const PRODUCT_STATUS_I18N = {
  [ProductStatus.Draft]: 'products.draft',
  [ProductStatus.Scheduled]: 'products.scheduled',
  [ProductStatus.Published]: 'products.published',
  [ProductStatus.Unlisted]: 'products.unlisted',
} as const

export const BILLING_I18N = {
  [BillingType.Free]: 'products.free',
  [BillingType.Workspace]: 'products.workspace',
  [BillingType.Seat]: 'products.seat',
  [BillingType.Usage]: 'products.usage',
  [BillingType.Module]: 'products.module',
} as const

export const PERIOD_UNIT_I18N = {
  [PeriodUnit.Day]: 'products.periodDay',
  [PeriodUnit.Month]: 'products.periodMonth',
  [PeriodUnit.Year]: 'products.periodYear',
} as const

export const PERIOD_UNIT_LABEL_I18N = {
  [PeriodUnit.Day]: 'products.unitDay',
  [PeriodUnit.Month]: 'products.unitMonth',
  [PeriodUnit.Year]: 'products.unitYear',
} as const

const PERIOD_PHRASE = {
  card: {
    [PeriodUnit.Day]: ['products.cardDay', 'products.cardDays'],
    [PeriodUnit.Month]: ['products.cardMonth', 'products.cardMonths'],
    [PeriodUnit.Year]: ['products.cardYear', 'products.cardYears'],
  },
  head: {
    [PeriodUnit.Day]: ['products.headDay', 'products.headDays'],
    [PeriodUnit.Month]: ['products.headMonth', 'products.headMonths'],
    [PeriodUnit.Year]: ['products.headYear', 'products.headYears'],
  },
  short: {
    [PeriodUnit.Day]: ['products.headDay', 'products.headDays'],
    [PeriodUnit.Month]: ['products.listMo', 'products.listMos'],
    [PeriodUnit.Year]: ['products.listYr', 'products.listYrs'],
  },
} as const

export function periodPhraseKey(
  length: number,
  unit: PeriodUnit,
  style: 'card' | 'head' | 'short',
) {
  const pair = PERIOD_PHRASE[style][unit]
  return pair[length === 1 ? 0 : 1]
}

export const ListAction = {
  Edit: 'edit',
  Publish: 'publish',
  ListNow: 'listNow',
  Cancel: 'cancel',
  Relist: 'relist',
  Unlist: 'unlist',
  Duplicate: 'duplicate',
  Delete: 'delete',
} as const
export type ListAction = (typeof ListAction)[keyof typeof ListAction]

export function emptyI18n(en = ''): I18nText {
  return { en }
}

export function copyI18n(text?: I18nText | null): I18nText {
  return {
    en: text?.en ?? '',
    'zh-CN': text?.['zh-CN'],
    'zh-TW': text?.['zh-TW'],
    vi: text?.vi,
  }
}

export function defaultI18n(text?: I18nText | null) {
  return text?.[I18N_DEFAULT] ?? ''
}

export function uid() {
  return `tmp_${Math.random().toString(36).slice(2, 9)}`
}

export function clip(value: string, max: number) {
  return value.length > max ? value.slice(0, max) : value
}

export function moveItem<T>(list: T[], from: number, to: number) {
  if (from === to || from < 0 || to < 0) return list
  const next = [...list]
  const [item] = next.splice(from, 1)
  next.splice(to, 0, item)
  return next
}

export function utcWall(iso: string | null | undefined) {
  return iso ? dayjs.utc(iso).format(WALL) : ''
}

export function utcFromPicker(value: string | Dayjs | null) {
  if (!value) return null
  const wall = typeof value === 'string' ? value : value.format(WALL)
  return dayjs.utc(wall, WALL).toISOString()
}

export function isFutureUtc(iso: string | null | undefined, now = Date.now()) {
  return Boolean(iso && dayjs.utc(iso).valueOf() > now)
}

export function moneyOk(raw: string) {
  const n = Number(raw)
  return raw !== '' && Number.isFinite(n) && n >= 0.01
}

export function acceptMoney(raw: string) {
  return raw === '' || /^\d+(\.\d{0,2})?$/.test(raw)
}

export function acceptPositiveInt(raw: string) {
  return raw === '' || /^[1-9]\d*$/.test(raw)
}

export function periodKey(p: ProductPeriod) {
  return `${p.unit}:${p.length}`
}

export function periodDays(unit: PeriodUnit, length: number) {
  if (unit === PeriodUnit.Day) return length
  if (unit === PeriodUnit.Month) return length * 30
  return length * 360
}

const PERIOD_UNIT_ORDER = {
  [PeriodUnit.Day]: 0,
  [PeriodUnit.Month]: 1,
  [PeriodUnit.Year]: 2,
} as const

export function sortPeriods(list: ProductPeriod[]) {
  return [...list].sort((a, b) => {
    const byDays = periodDays(a.unit, a.length) - periodDays(b.unit, b.length)
    return byDays !== 0 ? byDays : PERIOD_UNIT_ORDER[a.unit] - PERIOD_UNIT_ORDER[b.unit]
  })
}

export function periodPrice(monthly: number, period: ProductPeriod) {
  const months =
    period.unit === PeriodUnit.Day
      ? period.length / 30
      : period.unit === PeriodUnit.Month
        ? period.length
        : period.length * 12
  return Math.floor(monthly * months * 100 + 1e-9) / 100
}

export function formatUsd(n: number) {
  return `$${n.toFixed(2).replace(/\.?0+$/, '')}`
}

export function needsPeriods(type: BillingType) {
  return (
    type === BillingType.Workspace || type === BillingType.Seat || type === BillingType.Module
  )
}

export function billingLocked(status: ProductStatus) {
  return status !== ProductStatus.Draft
}

export function canDeletePeriod(status: ProductStatus, isNew: boolean) {
  return status === ProductStatus.Draft || isNew
}

export function listTimeReadonly(status: ProductStatus) {
  return status === ProductStatus.Published
}

function toDays(value: number, unit: PeriodUnit) {
  if (unit === PeriodUnit.Day) return value
  if (unit === PeriodUnit.Month) return value * 30
  return value * 360
}

function fromDays(days: number): { value: string; unit: PeriodUnit } {
  if (days >= 360 && days % 360 === 0) return { value: String(days / 360), unit: PeriodUnit.Year }
  if (days >= 30 && days % 30 === 0) return { value: String(days / 30), unit: PeriodUnit.Month }
  return { value: String(days || 1), unit: PeriodUnit.Day }
}

export function emptyTier(): FormTier {
  return {
    key: uid(),
    name: emptyI18n(),
    description: emptyI18n(),
    price: '',
    expire_value: '1',
    expire_unit: PeriodUnit.Month,
  }
}

export function emptyModule(): FormModule {
  return {
    key: uid(),
    name: emptyI18n(),
    description: emptyI18n(),
    price: '',
  }
}

export function defaultForm(): FormState {
  return {
    name: emptyI18n(),
    description: emptyI18n(),
    billing_type: BillingType.Free,
    effective_mode: EffectiveMode.AfterExpiry,
    effective_at: null,
    planPrice: '',
    tiers: [],
    modules: [],
    periods: [],
    listNow: true,
    list_at: null,
    unlistOn: false,
    unlist_at: null,
  }
}

export function fromProduct(p: SubscriptionProduct): FormState {
  return {
    name: copyI18n(p.name),
    description: copyI18n(p.description),
    billing_type: p.billing_type,
    effective_mode: p.effective_mode,
    effective_at: p.effective_at,
    planPrice: p.plans?.[0]?.price != null ? String(p.plans[0].price) : '',
    tiers: (p.tiers ?? []).map((tier) => {
      const exp = fromDays(tier.expire_days)
      return {
        key: tier.id,
        id: tier.id,
        name: copyI18n(tier.name),
        description: copyI18n(tier.description),
        price: String(tier.price ?? ''),
        expire_value: exp.value,
        expire_unit: exp.unit,
      }
    }),
    modules: (p.modules ?? []).map((mod) => ({
      key: mod.id,
      id: mod.id,
      name: copyI18n(mod.name),
      description: copyI18n(mod.description),
      price: String(mod.price ?? ''),
    })),
    periods: p.periods ?? [],
    listNow: p.status === ProductStatus.Scheduled ? false : !p.list_at,
    list_at: p.list_at,
    unlistOn: Boolean(p.unlist_at),
    unlist_at: p.unlist_at,
  }
}

export function toPayload(form: FormState): Partial<SubscriptionProduct> {
  const body: Partial<SubscriptionProduct> = {
    name: form.name,
    description: form.description,
    billing_type: form.billing_type,
    effective_mode: form.effective_mode,
    effective_at:
      form.effective_mode === EffectiveMode.Scheduled ? form.effective_at : null,
    list_at: form.listNow ? null : form.list_at,
    unlist_at: form.unlistOn ? form.unlist_at : null,
  }
  if (form.billing_type === BillingType.Workspace || form.billing_type === BillingType.Seat) {
    body.plans = [{ id: 'plan_main', price: Number(form.planPrice) || 0 }]
    body.periods = form.periods
    body.tiers = undefined
    body.modules = undefined
  } else if (form.billing_type === BillingType.Usage) {
    body.tiers = form.tiers.map((tier, i) => ({
      id: tier.id ?? `tier_${i}`,
      name: tier.name,
      description: tier.description,
      price: Number(tier.price) || 0,
      expire_days: toDays(Number(tier.expire_value) || 1, tier.expire_unit),
      sort_order: i,
    }))
    body.periods = undefined
    body.plans = undefined
    body.modules = undefined
  } else if (form.billing_type === BillingType.Module) {
    body.modules = form.modules.map((mod, i) => ({
      id: mod.id ?? `mod_${i}`,
      name: mod.name,
      description: mod.description,
      price: Number(mod.price) || 0,
      sort_order: i,
    }))
    body.periods = form.periods
    body.plans = undefined
    body.tiers = undefined
  } else {
    body.plans = undefined
    body.tiers = undefined
    body.modules = undefined
    body.periods = undefined
  }
  return body
}

export function applyBillingSwitch(form: FormState, next: BillingType): FormState {
  const copy = { ...form, billing_type: next }
  if (next === BillingType.Usage && copy.tiers.length === 0) copy.tiers = [emptyTier()]
  if (next === BillingType.Module && copy.modules.length === 0) copy.modules = [emptyModule()]
  return copy
}

export function tryAddPeriod(periods: ProductPeriod[], candidate: ProductPeriod) {
  if (periods.some((p) => periodKey(p) === periodKey(candidate))) return null
  return sortPeriods([...periods, candidate])
}

export function collectPublishErrors(
  form: FormState,
  status: ProductStatus,
  now = Date.now(),
): Record<string, boolean> {
  const next: Record<string, boolean> = {}
  if (!defaultI18n(form.name).trim()) next[FieldError.Name] = true
  if (form.billing_type === BillingType.Workspace || form.billing_type === BillingType.Seat) {
    if (!moneyOk(form.planPrice)) next[FieldError.PlanPrice] = true
    if (form.periods.length === 0) next[FieldError.Periods] = true
  }
  if (form.billing_type === BillingType.Usage) {
    if (form.tiers.length === 0) next[FieldError.Tiers] = true
    markUniqueNameErrors(form.tiers, RowError.TierName, next)
    form.tiers.forEach((tier, i) => {
      if (!moneyOk(tier.price)) next[rowError(RowError.TierPrice, i)] = true
      if (!tier.expire_value || Number(tier.expire_value) < 1) {
        next[rowError(RowError.TierExpire, i)] = true
      }
    })
  }
  if (form.billing_type === BillingType.Module) {
    if (form.modules.length === 0) next[FieldError.Modules] = true
    markUniqueNameErrors(form.modules, RowError.ModuleName, next)
    form.modules.forEach((mod, i) => {
      if (!moneyOk(mod.price)) next[rowError(RowError.ModulePrice, i)] = true
    })
    if (form.periods.length === 0) next[FieldError.Periods] = true
  }
  if (
    form.effective_mode === EffectiveMode.Scheduled &&
    !isFutureUtc(form.effective_at, now)
  ) {
    next[FieldError.EffectiveAt] = true
  }
  if (!form.listNow && !isFutureUtc(form.list_at, now)) next[FieldError.ListAt] = true
  if (form.unlistOn) {
    if (!form.unlist_at) next[FieldError.UnlistAt] = true
    else {
      const listMs =
        form.listNow || status === ProductStatus.Published
          ? now
          : dayjs.utc(form.list_at ?? 0).valueOf()
      if (dayjs.utc(form.unlist_at).valueOf() <= listMs) next[FieldError.UnlistAt] = true
    }
  }
  return next
}

function markUniqueNameErrors(
  rows: { name: I18nText }[],
  prefix: RowError,
  next: Record<string, boolean>,
) {
  const seen = new Set<string>()
  rows.forEach((row, i) => {
    const name = defaultI18n(row.name).trim()
    if (!name || seen.has(name)) next[rowError(prefix, i)] = true
    else seen.add(name)
  })
}

export function publishIssue(
  errors: Record<string, boolean>,
  form: FormState,
  status: ProductStatus,
): PublishIssue | null {
  if (Object.keys(errors).length === 0) return null
  if (errors[FieldError.Tiers]) return PublishIssue.NeedTier
  if (errors[FieldError.Modules]) return PublishIssue.NeedModule
  if (errors[FieldError.Periods]) return PublishIssue.NeedPeriod
  if (errors[FieldError.EffectiveAt]) return PublishIssue.EffectiveAtFuture
  if (errors[FieldError.ListAt]) return PublishIssue.ListAtFuture
  if (errors[FieldError.UnlistAt]) {
    return form.listNow || status === ProductStatus.Published
      ? PublishIssue.UnlistAfterNow
      : PublishIssue.UnlistAfterList
  }
  const names =
    form.billing_type === BillingType.Usage
      ? form.tiers.map((x) => defaultI18n(x.name).trim()).filter(Boolean)
      : form.modules.map((x) => defaultI18n(x.name).trim()).filter(Boolean)
  if (names.length !== new Set(names).size) return PublishIssue.NameUnique
  return PublishIssue.Required
}

export function canPublishProduct(p: SubscriptionProduct, now = Date.now()): PublishIssue | null {
  if (!defaultI18n(p.name).trim()) return PublishIssue.Required
  if (p.billing_type === BillingType.Workspace || p.billing_type === BillingType.Seat) {
    if (p.plans?.[0]?.price == null || p.plans[0].price < 0.01) return PublishIssue.Required
    if (!p.periods?.length) return PublishIssue.NeedPeriod
  }
  if (p.billing_type === BillingType.Usage) {
    if (!p.tiers?.length) return PublishIssue.NeedTier
    if (p.tiers.some((tier) => !defaultI18n(tier.name).trim() || tier.price < 0.01 || !tier.expire_days)) {
      return PublishIssue.Required
    }
  }
  if (p.billing_type === BillingType.Module) {
    if (!p.modules?.length) return PublishIssue.NeedModule
    if (p.modules.some((mod) => !defaultI18n(mod.name).trim() || mod.price < 0.01)) {
      return PublishIssue.Required
    }
    if (!p.periods?.length) return PublishIssue.NeedPeriod
  }
  if (p.effective_mode === EffectiveMode.Scheduled && !isFutureUtc(p.effective_at, now)) {
    return PublishIssue.EffectiveAtFuture
  }
  if (p.unlist_at) {
    const listMs = isFutureUtc(p.list_at, now) ? dayjs.utc(p.list_at).valueOf() : now
    if (dayjs.utc(p.unlist_at).valueOf() <= listMs) return PublishIssue.UnlistAfterList
  }
  return null
}

export function classifySaveToast(
  status: ProductStatus,
  isNew: boolean,
  before: FormState,
  after: FormState,
): SaveToast {
  if (status === ProductStatus.Draft || isNew) return { key: SaveToastKey.DraftSaved }
  const priceChanged =
    before.planPrice !== after.planPrice ||
    JSON.stringify(before.tiers.map((x) => x.price)) !==
      JSON.stringify(after.tiers.map((x) => x.price)) ||
    JSON.stringify(before.modules.map((x) => x.price)) !==
      JSON.stringify(after.modules.map((x) => x.price))
  const beforeKeys = new Set(before.periods.map(periodKey))
  const appended = after.periods.some((p) => !beforeKeys.has(periodKey(p)))
  const priceOrPeriod = priceChanged || appended
  const otherChanged =
    JSON.stringify({
      n: before.name,
      d: before.description,
      e: before.effective_mode,
      at: before.effective_at,
      u: before.unlist_at,
      tiers: before.tiers.map(({ price: _p, ...rest }) => rest),
      mods: before.modules.map(({ price: _p, ...rest }) => rest),
    }) !==
    JSON.stringify({
      n: after.name,
      d: after.description,
      e: after.effective_mode,
      at: after.effective_at,
      u: after.unlist_at,
      tiers: after.tiers.map(({ price: _p, ...rest }) => rest),
      mods: after.modules.map(({ price: _p, ...rest }) => rest),
    })
  if (priceOrPeriod && otherChanged) return { key: SaveToastKey.SaveBoth }
  if (priceOrPeriod) return { key: SaveToastKey.SavePrice }
  if (after.unlistOn && after.unlist_at && before.unlist_at !== after.unlist_at) {
    return { key: SaveToastKey.UnlistSaved, time: utcWall(after.unlist_at) }
  }
  return { key: SaveToastKey.SaveOther }
}

export function displayName(p: SubscriptionProduct, lang: string) {
  if (lang.startsWith('zh-TW') && p.name['zh-TW']) return p.name['zh-TW']
  if (lang.startsWith('zh') && p.name['zh-CN']) return p.name['zh-CN']
  if (lang.startsWith('vi') && p.name.vi) return p.name.vi
  return p.name.en || '—'
}

export function periodSummary(
  p: SubscriptionProduct,
  format: (length: number, unit: PeriodUnit, many: boolean) => string,
  empty: string,
) {
  if (p.billing_type === BillingType.Free || p.billing_type === BillingType.Usage) {
    return empty
  }
  const periods = sortPeriods(p.periods ?? [])
  if (periods.length === 0) return empty
  const many = periods.length > 1
  return periods.map((item) => format(item.length, item.unit, many)).join(' / ')
}

export function groupProducts(items: SubscriptionProduct[]) {
  return PRODUCT_STATUSES
    .map((status) => {
      const rows = items.filter((p) => p.status === status)
      rows.sort((a, b) => {
        if (status === ProductStatus.Scheduled) {
          const byList = (a.list_at ?? '').localeCompare(b.list_at ?? '')
          return byList !== 0 ? byList : (b.updated_at ?? '').localeCompare(a.updated_at ?? '')
        }
        const byUpdated = (b.updated_at ?? '').localeCompare(a.updated_at ?? '')
        return byUpdated !== 0 ? byUpdated : (b.created_at ?? '').localeCompare(a.created_at ?? '')
      })
      return { status, items: rows }
    })
    .filter((g) => g.items.length > 0)
}

export function actionsForStatus(status: ProductStatus): ListAction[] {
  if (status === ProductStatus.Draft) {
    return [ListAction.Edit, ListAction.Publish, ListAction.Duplicate, ListAction.Delete]
  }
  if (status === ProductStatus.Scheduled) {
    return [ListAction.Edit, ListAction.ListNow, ListAction.Cancel, ListAction.Duplicate]
  }
  if (status === ProductStatus.Published) {
    return [ListAction.Edit, ListAction.Unlist, ListAction.Duplicate]
  }
  return [ListAction.Edit, ListAction.Relist, ListAction.Duplicate]
}

export function hasI18nExtra(text: I18nText) {
  return I18N_LANGS.some((code) => code !== I18N_DEFAULT && Boolean(text[code]?.trim()))
}
